import { useCallback, useContext, useEffect } from "react";

import { useLazyQuery } from "@apollo/client";

import { convertDateForFront } from "@/components/layout/modals/utils";
import GlobalContext from "@/contexts/Global/GlobalContext";
import UserContext from "@/contexts/User/UserContext";

import { getProfileForHook } from "./graphql/__generated__/getProfileForHook";
import { GET_PROFILE_QUERY } from "./graphql/GET_PROFILE_QUERY";
import { UseProfileOptions } from "./types";

// Хук useProfile выполняет следующие функции:
// 1. Получает данные профиля пользователя через GraphQL запрос getMyProfile
// 2. Автоматически обновляет все контексты приложения:
//    - UserContext: userID, UUID, balance, freeMinutesCount, email, phone, name, isLobbyEnabled
//    - GlobalContext: isExpert
// 3. Выполняется автоматически при авторизации пользователя (когда userID === null)
// 4. Использует политику кэширования "network-only" с fallback на "cache-first"
// 5. Возвращает данные профиля, состояние загрузки и ошибки для использования в компонентах

const useProfile = (options: UseProfileOptions = {}) => {
  const {
    autoFetch = true,
    fetchPolicy = "network-only",
    nextFetchPolicy = "cache-first",
  } = options;

  const {
    userID,
    setUserID,
    setUUID,
    setBalance,
    setFreeMinutesCount,
    setWbOfferStatus,
    setWbOfferMinutesAmount,
    setWbOfferPrice,
    setWbOfferProductId,
    setEmail,
    setPhone,
    setName,
    isUserLoggedIn,
    setIsLobbyEnabled,
    setTmpPhone,
    setDateOfBirth,
    setIsEmailVerified,
    setAnonymous,
    setGender,
  } = useContext(UserContext);

  const { setIsExpert } = useContext(GlobalContext);

  const [getProfile, { loading: profileLoading, error: profileError, data: profileData, refetch: refetchProfile,
  }] = useLazyQuery<getProfileForHook>(GET_PROFILE_QUERY, {
    fetchPolicy,
    nextFetchPolicy,
  });

  const fetchProfile = useCallback(() => {
    if (isUserLoggedIn) {
      getProfile();
    }
  }, [isUserLoggedIn, getProfile]);

  useEffect(() => {
    if (autoFetch && isUserLoggedIn && userID === null) {
      fetchProfile();
    }
  }, [autoFetch, isUserLoggedIn, userID, fetchProfile]);

  useEffect(() => {
    if (profileData?.getMyProfile && !profileLoading && !profileError) {
      const {
        user: {
          id,
          uuid,
          freeMinutesLeft,
          name,
          dateOfBirth,
          anonymous,
          gender,
        },
        balance,
        tmpPhone,
        email,
        phone,
        isExpert,
        ppAvailable,
        isEmailVerified,
        wbOffer,
      } = profileData.getMyProfile;

      setUserID(id);
      setUUID(uuid);
      setBalance(balance);
      setFreeMinutesCount(freeMinutesLeft);
      setWbOfferStatus(wbOffer?.status ?? null);
      setWbOfferMinutesAmount(wbOffer?.minutesAmount ?? null);
      setWbOfferPrice(wbOffer?.price ?? null);
      setWbOfferProductId(wbOffer?.productID ?? null);
      setIsExpert(isExpert);
      setIsLobbyEnabled(ppAvailable);
      setDateOfBirth(convertDateForFront(dateOfBirth));
      setIsEmailVerified(!!isEmailVerified);
      setAnonymous(!!anonymous);
      if (tmpPhone) setTmpPhone(tmpPhone);
      if (phone) setPhone(phone);
      if (name) setName(name);
      if (email) setEmail(email);
      if (gender)setGender(gender);
    }
  }, [profileData, profileLoading, profileError, setUserID, setUUID,
    setBalance, setFreeMinutesCount,
    setWbOfferStatus, setWbOfferMinutesAmount, setWbOfferPrice, setWbOfferProductId,
    setEmail, setPhone, setName,
    setIsExpert, setIsLobbyEnabled, setTmpPhone, setDateOfBirth,
    setIsEmailVerified, setAnonymous, setGender]);

  return {
    fetchProfile,
    refetchProfile,
    profileData: profileData?.getMyProfile,
    loading: profileLoading,
    error: profileError,
  };
};

export default useProfile;
